import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import {
  BarChart3, Users, Eye, Clock, MousePointerClick, Globe, Monitor,
  ArrowUpRight, ArrowDownRight, Activity, Zap, TrendingUp, Smartphone,
  RefreshCw, ChevronDown, Target, ArrowRight, ArrowUpDown, ArrowUp, ArrowDown
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const PERIOD_OPTIONS = [
  { value: 1, label: "Today" },
  { value: 7, label: "7 Days" },
  { value: 14, label: "14 Days" },
  { value: 30, label: "30 Days" },
  { value: 90, label: "90 Days" },
];

const CHART_COLORS = {
  ancient: ["#C5A55A", "#8B0000", "#DC143C", "#E8D5A3", "#FFD700", "#6B4423"],
  cyber: ["#22d3ee", "#a855f7", "#ec4899", "#06b6d4", "#7c3aed", "#f472b6"],
};

export default function StatsPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isCyber = theme === "agent_eros_1";
  const accent = isCyber ? "text-cyan-400" : "text-divine-gold";
  const accentBorder = isCyber ? "border-cyan-500/30" : "border-divine-gold/30";
  const colors = isCyber ? CHART_COLORS.cyber : CHART_COLORS.ancient;

  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [topPages, setTopPages] = useState([]);
  const [sources, setSources] = useState(null);
  const [devices, setDevices] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [conversions, setConversions] = useState(null);
  const [geo, setGeo] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [clickPage, setClickPage] = useState("");
  const [clickSort, setClickSort] = useState("desc");
  const [clickPages, setClickPages] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const clickParams = new URLSearchParams({ days: String(days), click_sort: clickSort });
      if (clickPage) clickParams.set("click_page", clickPage);
      const [dashRes, rtRes, pagesRes, srcRes, devRes, tlRes, convRes, geoRes] = await Promise.all([
        authAxios.get(`/analytics/dashboard?days=${days}`),
        authAxios.get("/analytics/realtime"),
        authAxios.get(`/analytics/top-pages?days=${days}`),
        authAxios.get(`/analytics/sources?days=${days}`),
        authAxios.get(`/analytics/devices?days=${days}`),
        authAxios.get(`/analytics/timeline?days=${days}`),
        authAxios.get(`/analytics/conversions?${clickParams}`),
        authAxios.get(`/analytics/geo?days=${days}`),
      ]);
      setDashboard(dashRes.data);
      setRealtime(rtRes.data);
      setTopPages(pagesRes.data.pages || []);
      setSources(srcRes.data);
      setDevices(devRes.data);
      setTimeline(tlRes.data.timeline || []);
      setConversions(convRes.data);
      setClickPages(convRes.data.click_pages || []);
      setGeo(geoRes.data.countries || []);
    } catch (err) {
      console.error("Stats fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [days, clickPage, clickSort]);

  // Realtime is now on-demand — the existing Refresh button (`fetchAll`)
  // pulls a fresh `/analytics/realtime` snapshot along with the rest of
  // the dashboard. The old 30s interval was quietly eating 2 req/min of
  // the per-user rate-limit budget with no user-visible benefit; the
  // "Active Now" card is a heartbeat pill, not a live counter.
  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (user?.role !== "admin") {
    return <div className="min-h-screen bg-divine-black flex items-center justify-center text-white/50">Admin access required</div>;
  }

  const d = dashboard || {};

  return (
    <div className="min-h-screen bg-divine-black text-white p-4 sm:p-6" data-testid="stats-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <BarChart3 className={`w-6 h-6 ${accent}`} />
            <h1 className="text-2xl font-cinzel" data-testid="stats-title">Analytics</h1>
            {realtime && (
              <span className="flex items-center gap-1.5 ml-3 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                {realtime.active_5m} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm text-white/70 cursor-pointer"
              data-testid="period-selector"
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-divine-black">{o.label}</option>
              ))}
            </select>
            <Button variant="ghost" size="sm" onClick={fetchAll} className="h-8 w-8 p-0" data-testid="refresh-stats">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/10 pb-1 overflow-x-auto">
          {[
            { key: "overview", label: "Overview", icon: Activity },
            { key: "pages", label: "Pages", icon: Eye },
            { key: "sources", label: "Sources", icon: Globe },
            { key: "devices", label: "Devices", icon: Monitor },
            { key: "conversions", label: "Conversions", icon: Target },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-t transition-colors ${activeTab === key ? `${accent} bg-white/5 border-b-2 ${isCyber ? "border-cyan-400" : "border-divine-gold"}` : "text-white/40 hover:text-white/60"}`}
              data-testid={`tab-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {loading && !dashboard ? (
          <div className="flex items-center justify-center py-20 text-white/30">Loading analytics...</div>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                {/* Metric Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <MetricCard icon={Users} label="Total Visitors" value={d.total_visitors} accent={accent} accentBorder={accentBorder} />
                  <MetricCard icon={Eye} label="Page Views" value={d.pageviews} accent={accent} accentBorder={accentBorder} />
                  <MetricCard icon={Zap} label="Active Now" value={realtime?.active_5m || 0} accent="text-emerald-400" accentBorder="border-emerald-500/30" pulse />
                  <MetricCard icon={ArrowUpRight} label="Bounce Rate" value={`${d.bounce_rate || 0}%`} accent={d.bounce_rate > 60 ? "text-red-400" : accent} accentBorder={accentBorder} />
                  <MetricCard icon={Clock} label="Avg Engagement" value={formatSeconds(d.avg_engagement_seconds)} accent={accent} accentBorder={accentBorder} />
                </div>

                {/* New vs Returning + Auth vs Anon */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <SmallStat label="New Visitors" value={d.new_visitors} sub={`of ${d.total_visitors} total`} icon={ArrowUpRight} accent={accent} accentBorder={accentBorder} />
                  <SmallStat label="Returning" value={d.returning_visitors} sub={`${d.total_visitors ? Math.round(d.returning_visitors / d.total_visitors * 100) : 0}% retention`} icon={TrendingUp} accent={accent} accentBorder={accentBorder} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <SmallStat label="Authenticated Sessions" value={d.auth_sessions} sub={`${d.sessions ? Math.round(d.auth_sessions / d.sessions * 100) : 0}% of sessions`} icon={Users} accent={accent} accentBorder={accentBorder} />
                  <SmallStat label="Anonymous Sessions" value={d.anon_sessions} sub="Landing page visitors" icon={Globe} accent={accent} accentBorder={accentBorder} />
                </div>

                {/* Timeline Chart */}
                <ChartCard title="Visitor Timeline" accentBorder={accentBorder}>
                  {timeline.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={timeline}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: "#12122A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6 }} />
                        <Area type="monotone" dataKey="views" stroke={colors[0]} fill={colors[0]} fillOpacity={0.15} strokeWidth={2} name="Page Views" />
                        <Area type="monotone" dataKey="visitors" stroke={colors[1]} fill={colors[1]} fillOpacity={0.1} strokeWidth={2} name="Unique Visitors" />
                        <Legend />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <EmptyState />}
                </ChartCard>

                {/* Hot Pages Now */}
                {realtime?.hot_pages?.length > 0 && (
                  <ChartCard title="Hot Pages Right Now" accentBorder={accentBorder}>
                    <div className="space-y-2">
                      {realtime.hot_pages.map((p) => (
                        <div key={p.path} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                          <span className="text-white/70 text-sm truncate flex-1">{p.path}</span>
                          <span className={`text-sm font-medium ${accent}`}>{p.views} views</span>
                        </div>
                      ))}
                    </div>
                  </ChartCard>
                )}

                {/* Geo */}
                {geo.length > 0 && (
                  <ChartCard title="Top Countries" accentBorder={accentBorder}>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={geo.slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis type="number" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} width={100} />
                        <Tooltip contentStyle={{ background: "#12122A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6 }} />
                        <Bar dataKey="sessions" fill={colors[0]} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
              </div>
            )}

            {/* PAGES TAB */}
            {activeTab === "pages" && (
              <div className="space-y-6">
                <ChartCard title="Top Pages" accentBorder={accentBorder}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 text-white/50 font-medium">Page</th>
                          <th className="text-right py-2 text-white/50 font-medium w-24">Views</th>
                          <th className="text-right py-2 text-white/50 font-medium w-24">Unique</th>
                          <th className="text-right py-2 text-white/50 font-medium w-24">Scroll %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topPages.length > 0 ? topPages.map((p) => (
                          <tr key={p.path} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-2.5 text-white/80 truncate max-w-xs">{p.path}</td>
                            <td className={`py-2.5 text-right font-medium ${accent}`}>{p.views}</td>
                            <td className="py-2.5 text-right text-white/60">{p.unique_visitors}</td>
                            <td className="py-2.5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${p.avg_scroll}%`, background: colors[0] }} />
                                </div>
                                <span className="text-white/40 text-xs w-8 text-right">{p.avg_scroll}%</span>
                              </div>
                            </td>
                          </tr>
                        )) : <tr><td colSpan="4" className="py-8 text-center text-white/30">No page data yet</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </ChartCard>

                {/* Entry Pages */}
                {sources?.entry_pages?.length > 0 && (
                  <ChartCard title="Top Entry Pages" accentBorder={accentBorder}>
                    <div className="space-y-2">
                      {sources.entry_pages.map((p) => (
                        <div key={p.path} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                          <span className="text-white/70 text-sm truncate flex-1">{p.path}</span>
                          <span className={`text-sm ${accent}`}>{p.count} sessions</span>
                        </div>
                      ))}
                    </div>
                  </ChartCard>
                )}
              </div>
            )}

            {/* SOURCES TAB */}
            {activeTab === "sources" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ChartCard title="Traffic Sources" accentBorder={accentBorder}>
                    {sources?.categories?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={sources.categories} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {sources.categories.map((cat, i) => <Cell key={cat.name} fill={colors[i % colors.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: "#12122A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <EmptyState />}
                  </ChartCard>

                  <ChartCard title="Top Referrers" accentBorder={accentBorder}>
                    {sources?.domains?.length > 0 ? (
                      <div className="space-y-2">
                        {sources.domains.map((d) => (
                          <div key={d.domain} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                            <span className="text-white/70 text-sm truncate flex-1">{d.domain}</span>
                            <span className={`text-sm ${accent}`}>{d.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : <EmptyState text="No referrer data yet" />}
                  </ChartCard>
                </div>
              </div>
            )}

            {/* DEVICES TAB */}
            {activeTab === "devices" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ChartCard title="Device Type" accentBorder={accentBorder}>
                    {devices?.devices?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={devices.devices} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {devices.devices.map((dev, i) => <Cell key={dev.name} fill={colors[i % colors.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: "#12122A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <EmptyState />}
                  </ChartCard>

                  <ChartCard title="Browser" accentBorder={accentBorder}>
                    {devices?.browsers?.length > 0 ? (
                      <div className="space-y-2">
                        {devices.browsers.map((b) => (
                          <div key={b.name} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                            <span className="text-white/70 text-sm">{b.name}</span>
                            <span className={`text-sm ${accent}`}>{b.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : <EmptyState />}
                  </ChartCard>

                  <ChartCard title="Operating System" accentBorder={accentBorder}>
                    {devices?.os?.length > 0 ? (
                      <div className="space-y-2">
                        {devices.os.map((o) => (
                          <div key={o.name} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                            <span className="text-white/70 text-sm">{o.name}</span>
                            <span className={`text-sm ${accent}`}>{o.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : <EmptyState />}
                  </ChartCard>
                </div>
              </div>
            )}

            {/* CONVERSIONS TAB */}
            {activeTab === "conversions" && (
              <div className="space-y-6">
                {/* Conversion Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard icon={Target} label="Signups" value={conversions?.signups || 0} accent="text-emerald-400" accentBorder="border-emerald-500/30" />
                  <MetricCard icon={ArrowDownRight} label="Abandoned" value={conversions?.abandoned || 0} accent="text-red-400" accentBorder="border-red-500/30" />
                  <MetricCard icon={MousePointerClick} label="Form Starts" value={conversions?.form_starts || 0} accent={accent} accentBorder={accentBorder} />
                  <MetricCard icon={TrendingUp} label="Conv. Rate" value={`${conversions?.conversion_rate || 0}%`} accent={accent} accentBorder={accentBorder} />
                </div>

                {/* Funnel */}
                {conversions?.funnel && (
                  <ChartCard title="Signup Funnel" accentBorder={accentBorder}>
                    <div className="flex items-center justify-center gap-2 py-6 flex-wrap">
                      {[
                        { label: "Visited /", value: conversions.funnel.page_visited },
                        { label: "Form Started", value: conversions.funnel.form_started },
                        { label: "Completed", value: conversions.funnel.signup_completed },
                      ].map((step, i, arr) => (
                        <div key={step.label} className="flex items-center gap-2">
                          <div className="text-center">
                            <div className={`text-2xl font-bold ${accent}`}>{step.value}</div>
                            <div className="text-xs text-white/40 mt-1">{step.label}</div>
                          </div>
                          {i < arr.length - 1 && <ArrowRight className="w-5 h-5 text-white/20 mx-2" />}
                        </div>
                      ))}
                    </div>
                    {conversions.funnel.signup_abandoned > 0 && (
                      <div className="text-center text-sm text-red-400/70 mt-2">
                        {conversions.funnel.signup_abandoned} abandoned signups
                      </div>
                    )}
                  </ChartCard>
                )}

                {/* Conversion by Source */}
                {conversions?.by_source?.length > 0 && (
                  <ChartCard title="Conversions by Traffic Source" accentBorder={accentBorder}>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={conversions.by_source}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="source" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
                        <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: "#12122A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6 }} />
                        <Bar dataKey="conversions" fill={colors[0]} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {/* Top Clicks */}
                <ChartCard title="Top Button & Link Clicks" accentBorder={accentBorder}
                  headerRight={
                    <div className="flex items-center gap-2">
                      <Select value={clickPage || "__all__"} onValueChange={(v) => setClickPage(v === "__all__" ? "" : v)}>
                        <SelectTrigger className="h-8 w-[160px] bg-white/5 border-white/10 text-white/60 text-xs" data-testid="click-page-filter">
                          <SelectValue placeholder="All Pages" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#12122A] border-white/10">
                          <SelectItem value="__all__" className="text-white/70 focus:bg-white/10 focus:text-white text-xs">All Pages</SelectItem>
                          {clickPages.map(p => <SelectItem key={p} value={p} className="text-white/70 focus:bg-white/10 focus:text-white text-xs">{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setClickSort(clickSort === "desc" ? "asc" : "desc")}
                        className="h-8 px-2 text-white/50 hover:text-white"
                        data-testid="click-sort-toggle"
                        title={clickSort === "desc" ? "Showing highest first" : "Showing lowest first"}
                      >
                        {clickSort === "desc" ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
                        <span className="text-xs ml-1">{clickSort === "desc" ? "High" : "Low"}</span>
                      </Button>
                    </div>
                  }
                >
                  {conversions?.clicks?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 text-white/50 font-medium">Element</th>
                            <th className="text-left py-2 text-white/50 font-medium">Page</th>
                            <th className="text-right py-2 text-white/50 font-medium w-20">Clicks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {conversions.clicks.map((c) => (
                            <tr key={`${c.path}-${c.text}`} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-2 text-white/80 truncate max-w-[200px]">{c.text || "-"}</td>
                              <td className="py-2 text-white/50 truncate max-w-[150px]">{c.path}</td>
                              <td className={`py-2 text-right font-medium ${accent}`}>{c.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <EmptyState text="No click data for this filter" />}
                </ChartCard>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ───
function MetricCard({ icon: Icon, label, value, accent, accentBorder, pulse }) {
  return (
    <div className={`border ${accentBorder} rounded-lg p-4 bg-white/[0.02]`} data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className="text-white/40 text-xs uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${accent} ${pulse ? "animate-pulse" : ""}`}>{value ?? 0}</div>
    </div>
  );
}

function SmallStat({ label, value, sub, icon: Icon, accent, accentBorder }) {
  return (
    <div className={`border ${accentBorder} rounded-lg p-4 bg-white/[0.02] flex items-center gap-4`}>
      <Icon className={`w-5 h-5 ${accent} flex-shrink-0`} />
      <div>
        <div className="text-white/40 text-xs uppercase tracking-wider">{label}</div>
        <div className="flex items-baseline gap-2">
          <span className={`text-xl font-bold ${accent}`}>{value ?? 0}</span>
          {sub && <span className="text-white/30 text-xs">{sub}</span>}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children, accentBorder, headerRight }) {
  return (
    <div className={`border ${accentBorder} rounded-lg p-5 bg-white/[0.02]`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm text-white/50 uppercase tracking-wider">{title}</h3>
        {headerRight}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ text = "No data yet" }) {
  return <div className="py-8 text-center text-white/20 text-sm">{text}</div>;
}

function formatSeconds(s) {
  if (!s || s <= 0) return "0s";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}
